<script setup>
// core
import {onMounted, onUnmounted, ref, watch} from 'vue'
import {useRouter} from "vue-router";
import {useSocketActivation} from "@/components/Telephony/api/useSocketActivation";
import {useStore} from "vuex";

// components
import QrGeneration from "@/components/Telephony/QrGeneration.vue";
import ActivationFirstStep from "@/components/Telephony/ActivationFirstStep.vue";
import TimeExpired from "@/components/Telephony/TimeExpired.vue";
import ViberBlocked from "@/components/Telephony/ViberBlocked.vue";
import ScanCode from "@/components/Telephony/ScanCode.vue";
import ActivationResult from "@/components/Telephony/ActivationResult.vue";

const store = useStore()
const qrLink = ref(null)
const router = useRouter()
const step = ref(1);
const {activate, subscribe, unsubscribe, socketResponse} = useSocketActivation()

const resultStatus = ref('')
const resultNumber = ref('')
const firstExtension = ref(null)
const isExtensionLoading = ref(false)

const EXTENSION_RETRY_DELAY = 3000
const EXTENSION_TIMEOUT = 60000
let extensionTimer = null
let extensionDeadline = 0

const startPusher = () => {
    activate()
    subscribe()
}

const showResult = (status, number) => {
    resultStatus.value = status
    resultNumber.value = number
    step.value = 5
    unsubscribe()
}

const stopExtensionPolling = () => {
    clearTimeout(extensionTimer)
    extensionTimer = null
    isExtensionLoading.value = false
}

const pollFirstExtension = async () => {
    try {
        firstExtension.value = await store.dispatch('telephony/getFirstExtension')
    } catch (e) {
        firstExtension.value = null
    }

    if (firstExtension.value) return stopExtensionPolling()

    if (Date.now() >= extensionDeadline) {
        stopExtensionPolling()
        resultStatus.value = 'next'
        return
    }

    extensionTimer = setTimeout(pollFirstExtension, EXTENSION_RETRY_DELAY)
}

const loadFirstExtension = () => {
    isExtensionLoading.value = true
    extensionDeadline = Date.now() + EXTENSION_TIMEOUT

    pollFirstExtension()
}

const onActivated = (number, isFirstActivation) => {
    if (!isFirstActivation) return showResult('next', number)

    showResult('first', number)
    loadFirstExtension()
}

const onNextStep = (ns) => {
    switch (ns || ns.value) {
        case "generationCode":
            step.value = 2
            break
        case "timeOver":
            step.value = 3
            unsubscribe()
            break
        case "activation":
            step.value = 1
            startPusher()
            break
        case "integration":
            return router.push({name: "Integration"});
        case "oops":
        default:
            router.push({name: "Oops"});
            break;
    }
}

watch(socketResponse, () => {
    switch (socketResponse.value.status) {
        case "Link to activate the number":
            qrLink.value = socketResponse.value.link
            step.value = 4
            break
        case "QR timeout":
            step.value = 3
            unsubscribe()
            break
        case "Number successfully activated":
            onActivated(socketResponse.value.number, socketResponse.value.is_first_activation)
            break
        case "Number already activated":
            showResult('exists', socketResponse.value.number)
            break
        case "Number activation initiated":
            step.value = 2
            break
        case "Number activation blocked by Viber":
            step.value = 6
            unsubscribe()
            break
    }
})

onMounted(() => {
    startPusher()
})

onUnmounted(() => {
    unsubscribe()
    stopExtensionPolling()
})
</script>

<template>
    <section class="activation">
        <!-- Transition, not TransitionGroup: only it honours out-in, so the steps never stack -->
        <Transition name="fade" mode="out-in">
            <ActivationFirstStep v-if="step === 1" @next-step="onNextStep"/>

            <QrGeneration v-else-if="step === 2" @next-step="onNextStep"/>

            <TimeExpired v-else-if="step === 3" @next-step="onNextStep"/>

            <ScanCode v-else-if="step === 4" :qr-link="qrLink" @next-step="onNextStep"/>

            <ActivationResult
                v-else-if="step === 5"
                :status="resultStatus"
                :number="resultNumber"
                :extension="firstExtension"
                :loading="isExtensionLoading"
            />

            <ViberBlocked v-else-if="step === 6" @next-step="onNextStep"/>
        </Transition>
    </section>
</template>

<style scoped lang="scss">
.activation {
    padding: 0 20px;

    // the same heading weight and spacing the messenger pages use
    :deep(.txt) {
        font-weight: 500;
        margin-bottom: 30px;
    }
}
</style>
